'use client'

import { useEffect, useRef } from 'react'
import { ditherPalette, THEME_CHANGE_EVENT } from '@/lib/dither-colors'

const STREAMS = 12
const DOTS = 170 // per stream
const ACTIVE_DOTS = 420 // the highlighted stream draws more of its pool
const LENS_DOTS = 2600
const DOT = 1.6
// Geometry is authored on a 460×250 box and scaled to the rendered width.
const BASE_WIDTH = 460

type Dot = { t: number; jx: number; jy: number; speed: number }

/**
 * "Twelve industries. One method." drawn in dither: twelve streams of dots
 * flow out of the industry grid into one lens. `active` (an industry index,
 * or null) brightens its stream and dims the rest. Canvas 2D; it starts
 * once on screen, sleeps offscreen or in a hidden tab, and draws a single
 * still frame under reduced motion.
 */
export function IndustryStreams({ active }: { active: number | null }) {
  const rootRef = useRef<HTMLDivElement>(null)
  const activeRef = useRef(active)
  const redrawRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    activeRef.current = active
    redrawRef.current?.()
  }, [active])

  useEffect(() => {
    const root = rootRef.current
    if (!root || typeof IntersectionObserver === 'undefined') return
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)')
    let canvas: HTMLCanvasElement | null = null
    let ctx: CanvasRenderingContext2D | null = null
    const lens = document.createElement('canvas')
    let width = 0
    let height = 0
    let dpr = 1
    let frame = 0
    let last = 0
    let visible = false
    let palette = ditherPalette()

    // Stable per-dot randomness: position along the stream, jitter, speed.
    const streams: Dot[][] = Array.from({ length: STREAMS }, () =>
      Array.from({ length: ACTIVE_DOTS }, () => ({
        t: Math.random(),
        jx: Math.random() - 0.5,
        jy: Math.random() - 0.5,
        speed: 0.05 + Math.random() * 0.04,
      })),
    )
    const lensDots = Array.from({ length: LENS_DOTS }, () => ({
      u: Math.random() * 2 - 1,
      v: Math.random() * 2 - 1,
      a: 0.55 + Math.random() * 0.4,
    }))

    function geometry() {
      const s = width / BASE_WIDTH
      return { s, cx: width - 100 * s, cy: height * 0.44, rx: 40 * s, ry: 26 * s }
    }

    function renderLens() {
      lens.width = Math.round(width * dpr)
      lens.height = Math.round(height * dpr)
      const l = lens.getContext('2d')
      if (!l) return
      const { cx, cy, rx, ry } = geometry()
      l.setTransform(dpr, 0, 0, dpr, 0, 0)
      l.fillStyle = palette.accent
      for (const { u, v, a } of lensDots) {
        if (Math.abs(v) >= 1 - u * u) continue // outside the almond
        l.globalAlpha = a
        l.fillRect(cx + u * rx, cy + v * ry, DOT, DOT)
      }
    }

    function measure() {
      if (!canvas) return
      width = root!.clientWidth
      height = root!.clientHeight
      dpr = Math.min(window.devicePixelRatio || 1, 2)
      canvas.width = Math.round(width * dpr)
      canvas.height = Math.round(height * dpr)
      renderLens()
    }

    function draw(now: number) {
      if (!ctx || !width || !height) return
      const { s, cx, cy, rx } = geometry()
      const focus = activeRef.current
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, width, height)
      for (let i = 0; i < STREAMS; i++) {
        const on = focus === i
        const dim = focus !== null && !on
        // Stream i leaves the grid's top edge and bends into the lens tip.
        const x0 = (20 + (i / (STREAMS - 1)) * 300) * s
        const y0 = height - 2
        const y1 = cy + 40 * s
        const x2 = cx - 120 * s
        const x3 = cx - rx + 6 * s
        ctx.fillStyle = on ? palette.accent : palette.delivery
        const dots = streams[i]
        const count = on ? ACTIVE_DOTS : DOTS
        for (let k = 0; k < count; k++) {
          const { t, jx, jy } = dots[k]
          const m = 1 - t
          const x = m * m * m * x0 + 3 * m * m * t * x0 + 3 * m * t * t * x2 + t * t * t * x3
          const y = m * m * m * y0 + 3 * m * m * t * y1 + 3 * m * t * t * cy + t * t * t * cy
          const j = m * 5 * s
          ctx.globalAlpha = on ? 0.35 + 0.6 * t : dim ? 0.06 + 0.2 * t : 0.12 + 0.35 * t
          ctx.fillRect(x + jx * j, y + jy * j, DOT, DOT)
        }
      }
      // The lens breathes slowly and brightens while a stream is lit.
      ctx.globalAlpha = focus === null ? 0.85 + 0.15 * Math.sin(now / 900) : 1
      ctx.drawImage(lens, 0, 0, width, height)
      ctx.globalAlpha = 1
    }

    function tick(now: number) {
      frame = 0
      const dt = last ? Math.min((now - last) / 1000, 1 / 20) : 0
      last = now
      for (const dots of streams)
        for (const dot of dots) {
          dot.t += dot.speed * dt
          if (dot.t >= 1) dot.t -= 1
        }
      draw(now)
      schedule()
    }

    function schedule() {
      if (!frame && visible && !motion.matches && !document.hidden)
        frame = requestAnimationFrame(tick)
    }

    function stop() {
      if (frame) cancelAnimationFrame(frame)
      frame = 0
      last = 0
    }

    // Motion keeps redrawing on its own; a still frame redraws on demand.
    redrawRef.current = () => {
      if (!frame) draw(performance.now())
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting
        if (!visible) return stop()
        if (!canvas) {
          const element = document.createElement('canvas')
          const context = element.getContext('2d')
          if (!context) return io.disconnect() // no canvas support: leave the box empty
          canvas = element
          ctx = context
          canvas.className = 'industry-streams-canvas'
          root.appendChild(canvas)
          measure()
        }
        draw(performance.now())
        schedule()
      },
      { rootMargin: '120px 0px' },
    )
    const resize = new ResizeObserver(() => {
      measure()
      draw(performance.now())
    })
    const onTheme = () => {
      palette = ditherPalette()
      renderLens()
      draw(performance.now())
    }
    const onVisibility = () => (document.hidden ? stop() : schedule())
    io.observe(root)
    resize.observe(root)
    window.addEventListener(THEME_CHANGE_EVENT, onTheme)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      stop()
      io.disconnect()
      resize.disconnect()
      window.removeEventListener(THEME_CHANGE_EVENT, onTheme)
      document.removeEventListener('visibilitychange', onVisibility)
      redrawRef.current = null
      root.replaceChildren()
    }
  }, [])

  return <div ref={rootRef} className="industry-streams" aria-hidden="true" />
}
